export const ROLE_NAME_MAP: Record<string, string> = {
  ZB: 'Reviewing Authority',
  MB: 'Accepting Authority',
  ZA: 'Appraisee',
  RA: 'Reporting Authority',
};

export const parseDotNetDate = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const match = String(dateStr).match(/\/Date\((\d+)\)\//);
  if (match) {
    const timestamp = parseInt(match[1], 10);
    const date = new Date(timestamp);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }
  return String(dateStr);
};

export const parseDotDate = (value: string): Date | null => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value ?? '');
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    date.getFullYear() !== Number(yyyy) ||
    date.getMonth() !== Number(mm) - 1 ||
    date.getDate() !== Number(dd)
  ) {
    return null;
  }
  return date;
};

export const formatDotDate = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

export const toDotDate = (raw?: string | null): string => {
  if (!raw) return '-';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) return raw;
  const parsed = parseDotNetDate(raw);
  if (parsed && parsed !== '-') return parsed.replace(/-/g, '.');
  return raw;
};

export const toApiDateTime = (dotDate: string, endOfDay = false): string => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(dotDate ?? '');
  if (!match) {
    return endOfDay ? '2026-12-31T23:59:59' : '2019-01-01T00:00:00';
  }
  const [, dd, mm, yyyy] = match;
  return `${yyyy}-${mm}-${dd}T${endOfDay ? '23:59:59' : '00:00:00'}`;
};

export const stripLeadingZeros = (value?: string | number | null): string => {
  const str = String(value ?? '').trim();
  if (!str) return '';
  const stripped = str.replace(/^0+/, '');
  return stripped || '0';
};

export const getAppraiseeIdFromStateOrStorage = (user: any): string => {
  let rawId =
    user?.details?.empId ??
    user?.empId ??
    user?.EmpID ??
    user?.EmployeeID ??
    user?.usrid ??
    user?.data?.user?.empId ??
    user?.data?.empId ??
    user?.id ??
    '';

  if (!rawId) {
    try {
      const keys = ['user', 'userData', 'userInfo', 'loginData', 'authData'];
      for (const key of keys) {
        const item = localStorage.getItem(key) || sessionStorage.getItem(key);
        if (item) {
          const parsed = typeof item === 'string' && item.startsWith('{') ? JSON.parse(item) : item;
          const found =
            parsed?.details?.empId ??
            parsed?.empId ??
            parsed?.EmpID ??
            parsed?.EmployeeID ??
            parsed?.usrid ??
            parsed?.data?.empId ??
            parsed?.data?.user?.empId;
          if (found) {
            rawId = found;
            break;
          }
        }
      }
      if (!rawId) {
        rawId = localStorage.getItem('empId') || localStorage.getItem('employeeId') || sessionStorage.getItem('empId') || '';
      }
    } catch (e) {
      console.error('Error reading employee ID from storage:', e);
    }
  }

  return stripLeadingZeros(rawId);
};

export const extractList = (res: any): any[] => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.data?.data)) return res.data.data.data;
  if (Array.isArray(res?.data?.data?.data?.data)) return res.data.data.data.data;

  // Fallback: search object properties for first array found up to depth 4
  const findFirstArray = (obj: any, depth = 0): any[] | null => {
    if (!obj || typeof obj !== 'object' || depth > 4) return null;
    if (Array.isArray(obj)) return obj;
    for (const key of Object.keys(obj)) {
      if (Array.isArray(obj[key])) return obj[key];
    }
    for (const key of Object.keys(obj)) {
      if (obj[key] && typeof obj[key] === 'object') {
        const found = findFirstArray(obj[key], depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  return findFirstArray(res) || [];
};

export const mapAppraisalDetails = (raw: any, listItem?: any) => {
  const further = Array.isArray(raw?.Further) ? raw.Further : [];
  return {
    appraisalId: raw?.AppraisalID || listItem?.appraisalId || '',
    templateId: raw?.TemplateID || listItem?.templateId || '',
    documentName:
      listItem?.appraisalDocName ||
      raw?.AppraisalName ||
      raw?.TemplateID ||
      '',
    status: raw?.StatusName || listItem?.appraisalStatus || '',
    substatus:
      raw?.SubstatusName ||
      raw?.SubStatusName ||
      listItem?.substatus ||
      '',
    reportingAuthority: raw?.ReportingAuthority || '',
    employeeName: raw?.EmployeeName || listItem?.appraiseeName || '',
    employeeNumber: raw?.EmployeeNumber || listItem?.appraiseeId || '',
    empId: stripLeadingZeros(
      raw?.EmployeeNumber || raw?.AppraiseeID || listItem?.appraiseeId || '',
    ),
    appraiseeId: stripLeadingZeros(
      raw?.AppraiseeID || raw?.EmployeeNumber || listItem?.appraiseeId || '',
    ),
    employeePhoto:
      raw?.EmployeePhoto ||
      raw?.employeePhoto ||
      raw?.photo ||
      raw?.Photo ||
      '',
    EmployeePhoto:
      raw?.EmployeePhoto ||
      raw?.employeePhoto ||
      raw?.photo ||
      raw?.Photo ||
      '',
    validityFrom: toDotDate(
      raw?.ValidityPeriodFrom || listItem?.periodFrom,
    ),
    validityTo: toDotDate(raw?.ValidityPeriodTo || listItem?.periodTo),
    appraisalDate: toDotDate(raw?.AppraisalDate),
    designation: raw?.Designation || '',
    department: raw?.Department || '',
    payscale: raw?.Payscale || '',
    dateOfJoining: toDotDate(raw?.DateOfJoining),
    dateOfAppointment: toDotDate(raw?.DateOfAppointmentToCurrentGrade),
    dateOfBirth: toDotDate(raw?.DateOfBirth),
    educationQualifications: raw?.EducationQualifications || '',
    qualificationsAcquiredYear: raw?.QualificationsAcquiredYear || '',
    earnedLeave: raw?.EarnedLeave || '',
    medicalLeave: raw?.MedicalLeave || '',
    otherLeaves: raw?.OtherLeaves || '',
    absencePeriod: raw?.AbsencePeriodInYear || '',
    medicalCheckupDone: raw?.MedicalCheckupDone || '',
    medicalCheckupDate: toDotDate(raw?.MedicalCheckupDate),
    annualPropertyReturnCurrentYear:
      raw?.AnnualPropertyReturnCurrentYear ||
      raw?.AnnualPropertyReturn ||
      raw?.APRCurrentYear ||
      raw?.AprCurrentYear ||
      raw?.APR ||
      raw?.Apr ||
      '',
    annualPropertyReturnSubmissionDate: toDotDate(
      raw?.AnnualPropertyReturnSubmissionDate ||
        raw?.APRSubmissionDate ||
        raw?.AprSubmissionDate ||
        raw?.APRDate ||
        raw?.AprDate ||
        raw?.AnnualPropertyReturnDate,
    ),
    desirableTrainings: raw?.DesirableTrainings || '',
    constraintsInAchievingTarget: raw?.ConstraintsInAchievingTarget || '',
    agreeWithEmployeeAssessment: raw?.AgreeWithEmployeeAssessment || '',
    stateOfHealth: raw?.StateOfHealth || '',
    integrity: raw?.Integrity || '',
    adherenceToDmrcValuesEthics: raw?.AdherenceToDMRCValuesEthics || '',
    reprimandedDispleasureMemo: raw?.ReprimandedDispleasureMemo || '',
    exceptionalTraitTalent: raw?.ExceptionalTraitTalent || '',
    trainingRecommended: raw?.TrainingRecommended || '',
    ratings: Array.isArray(raw?.Ratings) ? raw.Ratings : [],
    trainings: Array.isArray(raw?.Trainings) ? raw.Trainings : [],
    currentYearGoals: Array.isArray(raw?.CurrentYearGoals)
      ? raw.CurrentYearGoals
      : [],
    nextYearGoals: Array.isArray(raw?.NextYearGoals)
      ? raw.NextYearGoals
      : [],
    participants: further
      .filter((p: any) => p?.FurtherParticipants)
      .map((p: any) => ({
        name: p?.FurtherParticipants || '',
        role:
          p?.AppraisalRoleName ||
          ROLE_NAME_MAP[p?.AppraisalRole] ||
          p?.AppraisalRole ||
          '',
      })),
    raw,
  };
};

export const parseGoalItems = (goals: any): string[] => {
  if (!goals) return [];
  const list = Array.isArray(goals) ? goals : [goals];
  const results: string[] = [];

  for (const entry of list) {
    const raw =
      typeof entry === 'string'
        ? entry
        : entry?.Goal || entry?.goal || '';
    if (!raw || typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;

    if (/^1[\.\)]\s*/.test(trimmed)) {
      let currentIndex = 1;
      let remaining = trimmed.replace(/^1[\.\)]\s*/, '');
      while (true) {
        const nextIndex = currentIndex + 1;
        const regex = new RegExp(`${nextIndex}[\\.\\)](\\t|\\s+)`);
        const match = remaining.match(regex);
        if (!match || match.index === undefined) {
          if (remaining.trim()) {
            results.push(remaining.trim());
          }
          break;
        }
        const itemText = remaining.slice(0, match.index).trim();
        if (itemText) results.push(itemText);
        remaining = remaining.slice(match.index + match[0].length);
        currentIndex = nextIndex;
      }
    } else if (trimmed.includes('\n')) {
      const lines = trimmed
        .split('\n')
        .map(l => l.replace(/^\d+[\.\)]\s*/, '').trim())
        .filter(Boolean);
      results.push(...lines);
    } else {
      results.push(trimmed);
    }
  }
  return results;
};

export const formatBase64Photo = (raw?: any): string | null => {
  if (!raw) return null;
  let str = typeof raw === 'string' ? raw.trim() : '';
  if (!str) return null;

  str = str.replace(/^["']|["']$/g, '').trim();
  if (!str) return null;

  if (str.startsWith('http://') || str.startsWith('https://')) {
    return str;
  }

  const cleaned = str.replace(/[\r\n\s]+/g, '');
  if (!cleaned) return null;

  if (cleaned.startsWith('data:image/')) {
    return cleaned;
  }

  let mimeType = 'image/jpeg';
  if (cleaned.startsWith('/9j/')) {
    mimeType = 'image/jpeg';
  } else if (cleaned.startsWith('iVBOR')) {
    mimeType = 'image/png';
  } else if (cleaned.startsWith('R0lGOD')) {
    mimeType = 'image/gif';
  } else if (cleaned.startsWith('UklGR')) {
    mimeType = 'image/webp';
  }

  return `data:${mimeType};base64,${cleaned}`;
};

export const getInitials = (name: string) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
